// Build menu (GDD §7, §14): opened by clicking an owned city. Shows the city's slots — a solid chip in your colour per
// building standing in the city, then a dashed green OPEN chip per free slot (a new building takes one) — and every
// building with its cost; options the client can already tell would be rejected are disabled with the reason (a UX
// hint: the sim validates every build it receives). Pointing at or focusing an option reports it (onHover) for the
// Intel panel; closing the menu reports that nothing is hovered any more.

import { BUILDING_KEYS, BUILDINGS, CAPTURE, type BuildingKey } from '@taxwar/content'
import type { BuildingTypeId, CityIndex, MapCityView } from '@taxwar/net'
import type { MatchView } from '../match/contracts'
import { reportHover, type HoverListener } from './bars'
import { BUILDING_PRESENTATION } from './catalog'
import { button, el, TextSlot } from './dom'
import { formatMoney, formatWhole } from './format'
import { REJECT_COPY } from './rejects'
import { buildObjection, buildingKeyOf, buildingSlots } from './rules'
import type { WarMapIndex } from './warMap'

const CAPITAL_LABEL = 'Capital'
const OPEN_SLOT_CODE = '+'
const OPEN_SLOT_LABEL = 'OPEN'
/** Chips are made once for the city kind with the most slots; a city with fewer hides the rest. */
const MAX_SLOTS = Math.max(CAPTURE.citySlots, CAPTURE.capitalSlots)
/** Gap between the click point and the menu, and between the menu and the viewport edge. */
const ANCHOR_OFFSET_PX = 12

interface SlotChip {
  readonly element: HTMLElement
  readonly code: TextSlot
  readonly name: TextSlot
}

function slotChip(): SlotChip {
  const element = el('span', 'tw-build__slot')
  const code = el('span', 'tw-build__slot-code')
  const name = el('span', 'tw-build__slot-name')
  element.append(code, name)
  return { element, code: new TextSlot(code), name: new TextSlot(name) }
}

interface BuildOption {
  readonly key: BuildingKey
  readonly element: HTMLButtonElement
  readonly reason: TextSlot
}

export class BuildMenu {
  readonly element: HTMLElement
  private readonly title: TextSlot
  private readonly hp: TextSlot
  private readonly slots: readonly SlotChip[]
  private readonly options: readonly BuildOption[]
  private city: MapCityView | null = null
  private money = 0

  constructor(
    private readonly map: WarMapIndex,
    private readonly match: MatchView,
    onBuild: (building: BuildingKey, city: MapCityView) => void,
    private readonly onHover: HoverListener<BuildingKey>,
    signal: AbortSignal,
  ) {
    this.element = el('section', 'tw-build tw-panel')
    this.element.setAttribute('role', 'dialog')
    this.element.setAttribute('aria-label', 'Build menu')
    this.element.tabIndex = -1
    this.element.hidden = true
    const title = el('h2', 'tw-heading')
    const hp = el('span', 'tw-dim')
    const meta = el('div', 'tw-build__meta')
    meta.append(hp)
    const slotRow = el('div', 'tw-build__slots')
    slotRow.setAttribute('aria-label', 'Building slots')
    this.slots = Array.from({ length: MAX_SLOTS }, slotChip)
    for (const chip of this.slots) slotRow.append(chip.element)
    const list = el('div', 'tw-build__options')
    this.options = BUILDING_KEYS.map((key) => {
      const presentation = BUILDING_PRESENTATION[key]
      const option = button('tw-build__option', '')
      const reason = el('span', 'tw-build__reason')
      option.append(
        el('span', 'tw-build__code', presentation.code),
        el('span', 'tw-build__name', presentation.name),
        el('span', 'tw-build__cost', formatMoney(BUILDINGS[key].cost)),
        reason,
      )
      reportHover(option, key, onHover, signal)
      option.addEventListener('click', () => {
        const city = this.city
        if (city !== null) onBuild(key, city)
      }, { signal })
      list.append(option)
      return { key, element: option, reason: new TextSlot(reason) }
    })
    this.element.append(title, meta, slotRow, list)
    this.title = new TextSlot(title)
    this.hp = new TextSlot(hp)
  }

  get isOpen(): boolean {
    return this.city !== null
  }

  /** The city the menu is open on, or null while it is closed. */
  get openCity(): MapCityView | null {
    return this.city
  }

  /** Opens next to a screen point (the click that picked the city). */
  open(index: CityIndex, clientX: number, clientY: number): void {
    this.city = this.map.city(index)
    this.render()
    this.element.hidden = false
    // Measured once per open (a click), never per frame.
    const box = this.element.getBoundingClientRect()
    const left = Math.min(clientX + ANCHOR_OFFSET_PX, window.innerWidth - box.width - ANCHOR_OFFSET_PX)
    const top = Math.min(clientY + ANCHOR_OFFSET_PX, window.innerHeight - box.height - ANCHOR_OFFSET_PX)
    this.element.style.left = `${Math.max(ANCHOR_OFFSET_PX, left)}px`
    this.element.style.top = `${Math.max(ANCHOR_OFFSET_PX, top)}px`
  }

  /** A keyboard city-list selection moves focus into the same popup used by map clicks. */
  focusFirstOption(): void {
    const first = this.options.find((option) => !option.element.disabled)
    if (first === undefined) this.element.focus()
    else first.element.focus()
  }

  close(): void {
    this.city = null
    this.element.hidden = true
    // A hidden option gets no pointerleave: end its hover here.
    this.onHover(null)
  }

  /** A city changed: refresh if it is the open one, and close once it is no longer ours. */
  cityChanged(index: CityIndex): void {
    const city = this.city
    if (city === null || city.index !== index) return
    if (this.match.cityState(index).owner !== this.map.you) {
      this.close()
      return
    }
    this.render()
  }

  setMoney(money: number): void {
    this.money = money
    if (this.city !== null) this.renderAffordability()
  }

  private render(): void {
    const city = this.city
    if (city === null) return
    const state = this.match.cityState(city.index)
    this.title.set(city.capital ? `${city.name} · ${CAPITAL_LABEL}` : city.name)
    this.hp.set(`HP ${formatWhole(Math.ceil(state.hp))}/${formatWhole(state.maxHp)}`)
    this.renderSlots(buildingSlots(city), state.buildings)
    for (const option of this.options) {
      const objection = buildObjection(option.key, city, state, this.map.you)
      option.element.disabled = objection !== null
      option.reason.set(objection === null ? '' : REJECT_COPY[objection].short)
    }
    this.renderAffordability()
  }

  /** One chip per slot: each building standing in the city fills one, the rest are open. The sim counts slots; it does not number them. */
  private renderSlots(slots: number, buildings: readonly BuildingTypeId[]): void {
    for (let i = 0; i < this.slots.length; i++) {
      const chip = this.slots[i] as SlotChip
      chip.element.hidden = i >= slots
      const building = buildings[i]
      const occupied = building !== undefined
      chip.element.classList.toggle('is-open', !occupied)
      if (occupied) {
        const presentation = BUILDING_PRESENTATION[buildingKeyOf(building)]
        chip.code.set(presentation.code)
        chip.name.set(presentation.name)
      } else {
        chip.code.set(OPEN_SLOT_CODE)
        chip.name.set(OPEN_SLOT_LABEL)
      }
    }
  }

  private renderAffordability(): void {
    for (const option of this.options) option.element.classList.toggle('is-affordable', this.money >= BUILDINGS[option.key].cost)
  }
}
